import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { BookmarkSimple, Plus, Check } from "@phosphor-icons/react";
import { toast } from "sonner";

export function SaveToBoardChip({ listing }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [boards, setBoards] = useState([]);
  const [newName, setNewName] = useState("");
  const [saved, setSaved] = useState(false);

  const openSave = async () => {
    if (!user) {
      toast("Sign in to save trips");
      navigate("/login");
      return;
    }
    try {
      const { data } = await api.get("/boards");
      setBoards(data);
      setOpen(true);
    } catch {
      toast.error("Could not load your boards");
    }
  };

  const saveTo = async (boardId) => {
    try {
      await api.post(`/boards/${boardId}/listings`, { listing_id: listing.id });
      toast.success(`${listing.title} saved to board`);
      setSaved(true);
      setOpen(false);
    } catch {
      toast.error("Failed to save");
    }
  };

  const createAndSave = async () => {
    if (!newName.trim()) return;
    try {
      const { data } = await api.post("/boards", { name: newName });
      await saveTo(data.id);
      setNewName("");
    } catch {
      toast.error("Failed to create board");
    }
  };

  return (
    <>
      <button
        onClick={openSave}
        className={`inline-flex items-center gap-1.5 pl-1.5 pr-3 py-1.5 rounded-full border text-sm font-medium transition-colors duration-200 ${
          saved
            ? "border-[#2E523A] bg-[#2E523A] text-white"
            : "border-[#D2D5CD] bg-white hover:border-[#2E523A]/50 text-[#1A1C19]"
        }`}
        data-testid={`chat-save-chip-${listing.id}`}
      >
        <img src={listing.image} alt="" className="h-6 w-6 rounded-full object-cover" />
        {listing.title}
        {saved ? <Check size={15} weight="bold" /> : <BookmarkSimple size={15} weight="duotone" className="text-[#2E523A]" />}
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-2xl">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Save {listing.title}</DialogTitle>
          </DialogHeader>
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {boards.length === 0 && (
              <p className="text-sm text-muted-foreground py-2">No boards yet — create your first below.</p>
            )}
            {boards.map((b) => (
              <button
                key={b.id}
                onClick={() => saveTo(b.id)}
                className="w-full text-left px-4 py-3 rounded-xl border border-[#D2D5CD] hover:border-[#2E523A] hover:bg-[#EAEBE6] transition-colors duration-200 font-medium"
                data-testid={`chat-board-option-${b.id}`}
              >
                {b.name}
              </button>
            ))}
          </div>
          <div className="flex gap-2 pt-2">
            <Input
              placeholder="New board name"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              data-testid="chat-new-board-input"
            />
            <Button onClick={createAndSave} className="rounded-full bg-[#2E523A] hover:bg-[#26442f] shrink-0" data-testid="chat-create-board-save">
              <Plus size={18} /> Create
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
